import { API_URL } from "src/scenes/App";

export const getDisplayName = (firstName, lastName, fallbackName) => {
    const displayName = [firstName, lastName].filter(Boolean).join(" ").trim();
    return displayName || fallbackName || "Unavailable user";
};

export const getInitials = (displayName) => {
    const words = displayName.trim().split(/\s+/).filter(Boolean);
    if (!words.length) {
        return "";
    }

    return words.slice(0, 2).map(word => word.charAt(0).toUpperCase()).join("");
};

export const getNoticeAuthorIdentity = (notice) => {
    if (!notice.postedByDetails) {
        return {
            displayName: notice.postedBy || "",
            profilePictureUrl: null
        };
    }

    return {
        displayName: notice.postedByDetails.employeeName || [
            notice.postedByDetails.firstName,
            notice.postedByDetails.lastName
        ].filter(Boolean).join(" ").trim() || notice.postedBy || "",
        profilePictureUrl: notice.postedByDetails.profilePictureUrl
    };
};

export const resolveProfilePictureUrl = (profilePictureUrl) => {
    if (!profilePictureUrl) {
        return null;
    }
    if (
        profilePictureUrl.startsWith("https://") ||
        profilePictureUrl.startsWith("http://") ||
        profilePictureUrl.startsWith("blob:") ||
        profilePictureUrl.startsWith("data:")
    ) {
        return profilePictureUrl;
    }
    if (profilePictureUrl.startsWith("//")) {
        return `https:${profilePictureUrl}`;
    }

    return `${API_URL.replace(/\/+$/, "")}/${profilePictureUrl.replace(/^\/+/, "")}`;
};

const REPLY_COMMENT_PREVIEW_LENGTH = 30;

export const getReplyPlaceholder = (comment) => {
    const commentPreview = comment.comment.replace(/\s+/g, " ").trim();
    const visibleComment = commentPreview.length > REPLY_COMMENT_PREVIEW_LENGTH ?
        `${commentPreview.slice(0, REPLY_COMMENT_PREVIEW_LENGTH).trimEnd()}...` :
        commentPreview;

    return `Reply to ${comment.firstName}: ${visibleComment}`;
};

export const sanitizeNoticeboardComment = (comment) => (
    comment.replace(/\r?\n(?:[^\S\r\n]*\r?\n){2,}/g, "\n\n")
);

export const getNoticePublication = (notice, audience) => {
    if (!Array.isArray(notice.publications)) {
        return null;
    }

    if (audience) {
        return notice.publications.find(publication => publication.readerType === audience) || null;
    }

    return notice.publications.length === 1 ? notice.publications[0] : null;
};

export const getNoticeInteractionUuid = (notice, audience) => {
    const publication = getNoticePublication(notice, audience);
    return publication ? publication.uuid : notice.uuid;
};

export const getNoticeCommentCount = (notice, audience) => {
    const publication = getNoticePublication(notice, audience);
    return publication ? publication.commentsCount : notice.commentsCount;
};

export const isNoticePinnedForAudience = (notice, audience) => {
    const publication = getNoticePublication(notice, audience);
    return publication ? publication.isPinned : notice.isPinned;
};

export const incrementNoticeCommentCount = (notice, interactionUuid) => ({
    ...notice,
    commentsCount: notice.commentsCount + 1,
    publications: notice.publications.map(publication => (
        publication.uuid === interactionUuid ?
            { ...publication, commentsCount: publication.commentsCount + 1 } :
            publication
    ))
});

export const setNoticePublicationPinned = (notice, publicationUuid, isPinned) => ({
    ...notice,
    publications: notice.publications.map(publication => (
        publication.uuid === publicationUuid ?
            { ...publication, isPinned } :
            publication
    ))
});

export const mergeNoticeFeedUpdate = (notice, updatedNotice) => ({
    ...notice,
    ...updatedNotice,
    postedByDetails: notice.postedByDetails,
    lastEditedByDetails: notice.lastEditedByDetails
});

export const getNoticeAudienceLabel = (notice, employeeName, clientName) => {
    const readerTypes = Array.isArray(notice.publications) ?
        notice.publications.map(publication => publication.readerType) :
        [];
    const labels = [];
    if (readerTypes.includes("employee")) {
        labels.push(employeeName);
    }
    if (readerTypes.includes("client")) {
        labels.push(clientName);
    }

    return labels.join(" & ");
};

export const getNoticeMediaItems = (notice) => {
    if (notice.postType === "image" && notice.image) {
        return [notice.image];
    }
    if (notice.postType === "video" && notice.video) {
        return [notice.video];
    }
    if (notice.postType === "carousel" && Array.isArray(notice.carousel)) {
        return notice.carousel;
    }

    return [];
};

export const getAttachmentType = (filename) => {
    const segments = filename.split(".");
    return segments.length > 1 ? segments.pop().toUpperCase() : "FILE";
};
